<template>
  <div class="studio-panel">
    <h2>Daily Drop</h2>
    <p class="studio-panel-subtitle">Your daily affirmation - a short, uplifting astrology thought drawn from your own sun + rising sign, written fresh each day in your voice.</p>

    <!-- Preview — today's drop image + affirmation caption, shown above the
         re-roll so the editor sees what seekers get. Eyebrow matches the
         community card (drop name + date). -->
    <section v-if="affirmation" class="aff-today">
      <div class="aff-over">
        <span class="aff-over-label">{{ dropNameLabel }}</span>
        <span class="aff-over-dot">·</span>
        <span class="aff-over-date">{{ affirmationDateLabel }}</span>
      </div>
      <div class="aff-hero" :class="{ 'is-refreshing': refreshingKind === 'image' }">
        <img
          v-if="affirmation.image_url"
          :key="'img-' + flashKey"
          :src="affirmation.preview_image_url || affirmation.image_url"
          class="aff-hero-img"
          alt="Today's affirmation illustration"
        />
        <div v-else class="aff-hero-noimg">✦</div>
        <div class="aff-caption">
          <p :key="flashKey" class="aff-caption-text aff-flash">{{ affirmation.message || '…' }}</p>
        </div>
      </div>
    </section>

    <div v-if="affirmation" class="aff-refresh-row">
      <button
        type="button"
        class="aff-refresh-btn"
        :disabled="!!refreshingKind"
        :title="msgNote"
        @click="refreshAffirmation('message')"
      >
        <span class="aff-refresh-ico" :class="{ spin: refreshingKind === 'message' }">↻</span>
        <span>{{ refreshingKind === 'message' ? 'Conjuring…' : msgLabel }}</span>
      </button>
      <button
        type="button"
        class="aff-refresh-btn"
        :disabled="!!refreshingKind"
        :title="imgNote"
        @click="refreshAffirmation('image')"
      >
        <span class="aff-refresh-ico" :class="{ spin: refreshingKind === 'image' }">↻</span>
        <span>{{ refreshingKind === 'image' ? 'Painting…' : imgLabel }}</span>
      </button>
      <span v-if="affirmationError" class="studio-error aff-err">{{ affirmationError }}</span>
    </div>
    <div v-else-if="affirmationLoading" class="studio-loading">
      <span class="studio-loading-dot"></span>
      <span class="studio-loading-text">Loading today's affirmation…</span>
    </div>

    <div v-if="loading" class="studio-loading">
      <span class="studio-loading-dot"></span>
      <span class="studio-loading-text">Loading your daily drop…</span>
    </div>
    <div v-if="loadError" class="studio-error">{{ loadError }}</div>

    <template v-if="!loading && setting">
      <!-- Drop name — the public label seekers see (top-left on your community
           About page). -->
      <div v-if="group" class="drop-name-field">
        <label class="drop-name-label">
          Drop name
          <span class="drop-name-hint"> - what seekers see above this drop on your community page</span>
        </label>
        <input
          class="drop-name-input"
          type="text"
          :value="group.drop_horoscope_alias || ''"
          placeholder="e.g. Mystic Lee Daily"
          @input="group.drop_horoscope_alias = $event.target.value"
          @blur="saveDropName"
        />
        <span v-if="dropNameSaved" class="beat-card-saved">Saved</span>
      </div>

      <div v-for="f in FIELDS" :key="f.key" class="beat-field">
        <div class="beat-card" :class="{ 'beat-card-active': isActive(f), 'beat-card-expanded': isExpanded(f.key) }">
          <div class="beat-card-header" @click="toggle(f.key)">
            <span class="beat-head-left">
              <span class="beat-chevron" :class="{ open: isExpanded(f.key) }">▸</span>
              <span class="beat-label-text">{{ f.label }}</span>
              <span class="beat-label-help" @click.stop>
                <FieldHelpIcon :field="f.fieldKey" :label="f.label" panel="syndicate" :value="setting[f.prop] || ''" />
              </span>
            </span>
          </div>

          <div v-show="isExpanded(f.key)" class="beat-card-body">
            <!-- Message length — the same word/char cap control as the reading beats. -->
            <div v-if="f.word" class="beat-limit" title="How long the daily message runs (words)">
              <input
                type="number" class="beat-limit-input"
                min="5" max="20" step="1"
                v-model.number="setting.drop_message_word_count"
                @blur="onWordBlur"
                aria-label="Message length in words"
              />
              <span class="beat-limit-suffix">Words</span>
            </div>

            <p class="beat-help">{{ f.help }}</p>

            <!-- Edit it yourself directly, or hand it to Aida with Refine. -->
            <textarea
              class="beat-textarea"
              :value="setting[f.prop] || ''"
              :placeholder="f.placeholder"
              rows="3"
              @input="setting[f.prop] = $event.target.value"
              @blur="persist(f.key)"
            ></textarea>

            <div class="beat-card-actions">
              <button type="button" class="beat-btn beat-btn-refine" @click="refine(f)">
                ✨ {{ setting[f.prop] ? 'Refine with Aida' : 'Write with Aida' }}
              </button>
              <span v-if="savedKey === f.key" class="beat-card-saved">Saved</span>
              <span v-if="saveError" class="studio-error">{{ saveError }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="!loading && !loadError && !setting" class="studio-stub">
      <p>No daily drop found.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { store, static_store } from '@/store'
import { parseAxiosError, clientToday } from '@/utils/general-utils'
import FieldHelpIcon from '../FieldHelpIcon.vue'
import { dispatchFieldContext, studioAssistantState } from '@/utils/studio-assistant'

const FIELDS = [
  {
    key: 'message', fieldKey: 'drop:message', label: 'Message Guidance', prop: 'drop_prompt', word: true,
    help: "The tone & focus for your daily affirmation - Aida writes a fresh one each day in your voice, drawn from your sun + rising sign.",
    placeholder: 'e.g. A grounding nudge toward courage and small brave steps.',
  },
  {
    key: 'illustration', fieldKey: 'drop:illustration', label: 'Illustration', prop: 'illustration_instructions',
    help: "The art direction for today's image - Aida paints it from this. Seeded from your reader's illustrative style; change it up here anytime.",
    placeholder: "e.g. Dreamy watercolor, moonlit, deep indigo and gold.",
  },
]

const setting = ref(null)
const group = ref(null)
const loading = ref(false)
const loadError = ref('')
const saveError = ref('')
const savedKey = ref(null)
const dropNameSaved = ref(false)
const expanded = reactive({ message: false, illustration: false })

// Affirmation of the day — actual generated drop + manual re-roll allowance.
const affirmation = ref(null)
const affirmationLoading = ref(false)
const refreshingKind = ref('')   // '' | 'message' | 'image'
const affirmationError = ref('')
// Bumped whenever the affirmation text changes so the message element remounts
// and replays the flash animation — makes a refresh visibly land.
const flashKey = ref(0)

// Eyebrow shown over the affirmation — drop name + date, matching the community
// card (which reads today_drop.label = the group's drop_horoscope_alias).
const dropNameLabel = computed(() => (group.value?.drop_horoscope_alias || '').trim() || "Today's Affirmation")
const affirmationDateLabel = computed(() => {
  const raw = affirmation.value?.drop_date
  const d = raw ? new Date(`${raw}T00:00:00`) : new Date()
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
})

// Per-piece re-roll labels/notes — the message and image refresh independently,
// each with its own 3-free-then-cost allowance from the payload.
function pieceLabel(noun, p) {
  if (!p) return `Refresh ${noun}`
  return p.free_remaining > 0
    ? `Refresh ${noun} (${p.free_remaining} free)`
    : `Refresh ${noun} · ${p.cost} carats`
}
function pieceNote(p, balance) {
  if (!p) return ''
  if (p.free_remaining > 0) return `${p.free_remaining} of ${p.free_limit} free left today`
  return `Out of free - ${p.cost} carats each · balance ${balance} carats`
}
const msgLabel = computed(() => pieceLabel('message', affirmation.value?.message_refresh))
const imgLabel = computed(() => pieceLabel('image', affirmation.value?.image_refresh))
const msgNote = computed(() => pieceNote(affirmation.value?.message_refresh, affirmation.value?.emerald_balance))
const imgNote = computed(() => pieceNote(affirmation.value?.image_refresh, affirmation.value?.emerald_balance))

function syncBalance() {
  if (affirmation.value && store.reader && typeof affirmation.value.emerald_balance === 'number') {
    store.reader.emerald_balance = affirmation.value.emerald_balance
  }
}

async function loadAffirmation() {
  affirmationLoading.value = true
  affirmationError.value = ''
  try {
    const res = await axios.get(`${static_store.apiUrl}/reader/daily-affirmation/`, { headers: authHeaders(), params: { date: clientToday() } })
    affirmation.value = res.data
    syncBalance()
  } catch (error) {
    affirmationError.value = parseAxiosError(error)
  } finally {
    affirmationLoading.value = false
  }
}

async function refreshAffirmation(kind) {
  if (refreshingKind.value) return
  refreshingKind.value = kind   // 'message' | 'image'
  affirmationError.value = ''
  try {
    const res = await axios.post(`${static_store.apiUrl}/reader/daily-affirmation/refresh/`, { type: kind }, { headers: authHeaders(), params: { date: clientToday() } })
    affirmation.value = res.data
    flashKey.value += 1
    syncBalance()
  } catch (error) {
    if (error?.response?.status === 402) {
      // Merge the counters back so the note reflects the (unchanged) balance.
      affirmation.value = { ...(affirmation.value || {}), ...(error.response.data || {}) }
      affirmationError.value = `You're out of free ${kind} refreshes and carats for today.`
    } else {
      affirmationError.value = parseAxiosError(error)
    }
  } finally {
    refreshingKind.value = ''
  }
}

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

function toggle(key) { expanded[key] = !expanded[key] }
function isExpanded(key) { return !!expanded[key] }
function isActive(f) { return studioAssistantState.open && studioAssistantState.activeField?.field === f.fieldKey }

// "Refine / Write" → open the Mystic Aida assistant scoped to this field, the
// same path the reading-session beats use.
function refine(f) {
  dispatchFieldContext({
    field: f.fieldKey, label: f.label, panel: 'syndicate',
    value: setting.value?.[f.prop] || '', source: 'click',
  })
}

// Aida finished shaping a field → write it back + persist. Fires via the shared
// `studio-assistant:polish-applied` event (same channel as the reading beats).
function onPolishApplied(e) {
  const { field, value } = e.detail || {}
  if (!setting.value || value === undefined) return
  const f = FIELDS.find(x => x.fieldKey === field)
  if (!f) return
  setting.value[f.prop] = value
  persist(f.key)
}

function onWordBlur() {
  if (!setting.value) return
  let v = parseInt(setting.value.drop_message_word_count, 10)
  if (Number.isNaN(v)) v = 12
  setting.value.drop_message_word_count = Math.max(5, Math.min(20, v))
  persist('message')
}

async function persist(key) {
  if (!setting.value) return
  saveError.value = ''
  try {
    await axios.patch(`${static_store.apiUrl}/reader/update-drop-setting/${setting.value.id}/`, {
      drop_prompt: setting.value.drop_prompt,
      drop_message_word_count: setting.value.drop_message_word_count,
      illustration_instructions: setting.value.illustration_instructions || '',
    }, { headers: authHeaders() })
    savedKey.value = key
    setTimeout(() => { if (savedKey.value === key) savedKey.value = null }, 1500)
  } catch (error) {
    saveError.value = parseAxiosError(error)
  }
}

// Drop name = the public label seekers see on this drop (drop_horoscope_alias on
// the affirmation's group). Shown top-left on the Mystic's community About page.
async function saveDropName() {
  if (!group.value?.id) return
  saveError.value = ''
  try {
    await axios.patch(`${static_store.apiUrl}/reader/update-drop-group/${group.value.id}/`, {
      drop_horoscope_alias: group.value.drop_horoscope_alias || '',
    }, { headers: authHeaders() })
    dropNameSaved.value = true
    setTimeout(() => { dropNameSaved.value = false }, 1500)
  } catch (error) {
    saveError.value = parseAxiosError(error)
  }
}

onMounted(async () => {
  window.addEventListener('studio-assistant:polish-applied', onPolishApplied)
  loadAffirmation()
  loading.value = true
  try {
    const res = await axios.get(`${static_store.apiUrl}/reader/my-drop-settings/`, { headers: authHeaders() })
    // The slim config ONLY ever edits the AFFIRMATION (default) drop + its group —
    // the same setting the affirmation generator reads. Never fall back to a
    // zodiac/natal setting (Mystic Pro): a misdirected fallback would save to the
    // wrong place while generation kept using the untouched affirmation one. The
    // BE guarantees this exists (my_drop_settings → ensure_daily_drop_setting).
    for (const g of (res.data.groups || [])) {
      const s = (g.settings || []).find(x => x.drop_type === 'affirmation')
      if (s) { setting.value = s; group.value = g; break }
    }
    if (setting.value && setting.value.drop_message_word_count == null) {
      setting.value.drop_message_word_count = 12
    }
  } catch (error) {
    loadError.value = parseAxiosError(error)
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('studio-assistant:polish-applied', onPolishApplied)
})
</script>

<style scoped>
@import '@/styles/studio-forms.css';

/* Drop name — public label for the affirmation drop. */
.drop-name-field { margin-bottom: 16px; }
.drop-name-label {
  display: block; margin-bottom: 6px;
  font-family: 'Cinzel', serif; font-size: 13px; color: #C9A94E; font-weight: 600;
}
.drop-name-hint { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 400; font-style: italic; color: rgba(195, 169, 255, 0.45); letter-spacing: 0; }
.drop-name-input {
  width: 100%; box-sizing: border-box;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px; padding: 10px 12px; color: #E8D8FF;
  font-family: 'Inter', sans-serif; font-size: 14px; outline: none;
  transition: border-color 0.15s;
}
.drop-name-input::placeholder { color: rgba(255, 255, 255, 0.3); font-style: italic; }
.drop-name-input:focus { border-color: rgba(201, 169, 78, 0.5); }

/* Beat-card design — mirrors the Reading Sessions tab so every value editor
   looks the same. */
.beat-field { margin-bottom: 14px; }
.beat-card {
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: 'Inter', sans-serif;
}
.beat-card:hover { border-color: rgba(201, 169, 78, 0.3); }
.beat-card-active { border-color: rgba(201, 169, 78, 0.65); box-shadow: 0 0 0 1px rgba(201, 169, 78, 0.25); }

.beat-card-header {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 16px; cursor: pointer; user-select: none;
}
.beat-head-left { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.beat-chevron { color: rgba(201, 169, 78, 0.7); font-size: 12px; line-height: 1; transition: transform 0.15s; display: inline-block; }
.beat-chevron.open { transform: rotate(90deg); }
.beat-label-text { font-family: 'Cinzel', serif; font-size: 18px; letter-spacing: 0.05em; text-transform: uppercase; color: #C9A94E; font-weight: 600; }
.beat-label-help { display: inline-flex; align-items: center; margin-left: 6px; }

.beat-card-body { padding: 0 16px 14px; }
.beat-help { margin: 0 0 12px; font-size: 12px; color: rgba(195,169,255,0.45); line-height: 1.5; font-style: italic; }

.beat-limit { display: inline-flex; align-items: center; gap: 7px; flex: 0 0 auto; margin-bottom: 12px; }
.beat-limit-input {
  width: 46px; padding: 3px 6px; background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 5px; color: #E8D8FF;
  font-family: 'Inter', sans-serif; font-size: 11px; text-align: center; outline: none;
}
.beat-limit-input::-webkit-outer-spin-button,
.beat-limit-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.beat-limit-input { -moz-appearance: textfield; appearance: textfield; }
.beat-limit-input:focus { border-color: rgba(201, 169, 78, 0.5); }
.beat-limit-suffix { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: rgba(195, 169, 255, 0.5); }

.beat-card-text { display: block; color: #E8D8FF; font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
.beat-card-empty { display: block; color: rgba(255, 255, 255, 0.35); font-style: italic; font-size: 14px; line-height: 1.6; }
.beat-textarea {
  width: 100%; box-sizing: border-box; resize: vertical; min-height: 64px;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px; padding: 10px 12px; color: #E8D8FF;
  font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.6; outline: none;
  transition: border-color 0.15s;
}
.beat-textarea::placeholder { color: rgba(255, 255, 255, 0.3); font-style: italic; }
.beat-textarea:focus { border-color: rgba(201, 169, 78, 0.5); }

.beat-card-actions { display: flex; align-items: center; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.beat-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 8px;
  font-size: 13px; font-family: 'Inter', sans-serif; cursor: pointer; border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}
.beat-btn-refine { background: rgba(201, 169, 78, 0.14); border-color: rgba(201, 169, 78, 0.4); color: #E8DCB4; }
.beat-btn-refine:hover { background: rgba(201, 169, 78, 0.24); border-color: #C9A94E; }
.beat-card-saved { font-size: 11px; color: #50C7A8; }

/* Affirmation of the day — eyebrow top-left ABOVE an image tile with the
   affirmation centered over it, plus a refresh overlay (matches the community card). */
.aff-today { margin-bottom: 22px; }
.aff-over { display: flex; align-items: baseline; gap: 7px; margin: 0 0 8px; padding-left: 2px; }
.aff-over-label {
  font-family: 'Cinzel', serif; font-weight: 600; font-size: 12px;
  color: #FFDFA3; text-transform: uppercase; letter-spacing: 0.14em;
}
.aff-over-dot { color: rgba(195, 169, 255, 0.45); }
.aff-over-date {
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 13px; color: rgba(195, 169, 255, 0.7);
}
.aff-hero {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(201, 169, 78, 0.3);
  min-height: 230px;
  background: radial-gradient(640px 220px at 50% 0%, rgba(40, 22, 70, 0.6) 0%, rgba(10, 0, 21, 0.4) 75%);
}
.aff-hero-img { display: block; width: 100%; height: 300px; object-fit: cover; }
.aff-hero-noimg {
  display: flex; align-items: center; justify-content: center;
  height: 230px; font-size: 44px; color: rgba(201, 169, 78, 0.4);
}

/* Refresh overlay — top-right pill, brightens on hover of the tile. */
.aff-refresh-overlay {
  position: absolute; top: 12px; right: 12px; z-index: 2;
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: 999px;
  background: rgba(12, 4, 24, 0.62); backdrop-filter: blur(4px);
  border: 1px solid rgba(160, 120, 220, 0.45);
  color: #E8DDFF; font-family: 'Cinzel', serif; font-size: 12px; cursor: pointer;
  opacity: 0.82; transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}
.aff-hero:hover .aff-refresh-overlay { opacity: 1; background: rgba(102, 51, 153, 0.55); border-color: rgba(160, 120, 220, 0.85); }
.aff-refresh-overlay:disabled { opacity: 0.9; cursor: default; }
.aff-refresh-label { white-space: nowrap; }
.aff-refresh-ico { font-size: 14px; line-height: 1; display: inline-block; }
.aff-refresh-ico.spin { animation: aff-spin 0.9s linear infinite; }
@keyframes aff-spin { to { transform: rotate(360deg); } }
.aff-hero.is-refreshing .aff-hero-img { filter: brightness(0.6) saturate(0.8); transition: filter 0.2s; }

/* Standalone re-roll — the image preview moved to the Identity hero, so the
   re-roll lives here as a slim pill instead of an on-image overlay. */
.aff-refresh-row { display: flex; align-items: center; gap: 10px; margin: 4px 0 18px; }
.aff-refresh-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 16px; border-radius: 999px;
  background: rgba(102, 51, 153, 0.28);
  border: 1px solid rgba(160, 120, 220, 0.45);
  color: #E8DDFF; font-family: 'Cinzel', serif; font-size: 12px; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.aff-refresh-btn:hover { background: rgba(102, 51, 153, 0.45); border-color: rgba(160, 120, 220, 0.8); }
.aff-refresh-btn:disabled { opacity: 0.7; cursor: default; }

/* Caption — affirmation text centered over a bottom scrim. */
.aff-caption {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 1;
  padding: 44px 20px 18px;
  text-align: center;
  background: linear-gradient(to top, rgba(8, 2, 18, 0.92) 0%, rgba(8, 2, 18, 0.7) 45%, rgba(8, 2, 18, 0) 100%);
}
.aff-caption-text {
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 21px; line-height: 1.45; color: #f6eed8;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
}
.aff-err { display: inline-block; margin-top: 8px; }
/* Plays when the caption remounts (flashKey bump) so a fresh affirmation is
   unmistakable even when it lands on a similar theme. */
.aff-flash { animation: aff-flash 0.7s ease-out; }
@keyframes aff-flash {
  0% { opacity: 0; transform: translateY(4px); color: #FFDFA3; }
  60% { color: #FFDFA3; }
  100% { opacity: 1; transform: translateY(0); color: #f6eed8; }
}
</style>
